import React, { useState } from 'react';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db, auth } from './firebase';
import './ReactivateStudent.css';

function ReactivateStudent({ student, onClose }) {
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');

  const handleReactivate = async () => {
    if (!window.confirm(
      `Are you sure you want to reactivate ${student.studentName}?\n\n` +
      `Current Balance Owed: R${(student.totalOwing || 0).toFixed(2)}\n\n` +
      `The student will become active again and their financial history will remain intact.`
    )) {
      return;
    }

    setLoading(true);

    try {
      const user = auth.currentUser;
      const studentRef = doc(db, 'students', student.id);

      const reactivationEntry = {
        reactivatedDate: new Date(),
        reactivatedBy: user.email,
        reason: notes.trim() || 'Student reactivated',
        previousDeactivation: student.deactivationInfo || null
      };

      await updateDoc(studentRef, {
        isActive: true,
        reactivationHistory: arrayUnion(reactivationEntry),
        updatedAt: new Date()
        // Note: Keep deactivationInfo for historical record
      });

      alert(`${student.studentName} has been successfully reactivated!`);
      onClose();
    } catch (error) {
      console.error('Error reactivating student:', error);
      alert('Error reactivating student. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `R${(amount || 0).toFixed(2)}`;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (date.toDate) {
      return date.toDate().toLocaleDateString();
    }
    return new Date(date).toLocaleDateString();
  };

  return (
    <div className="modal-overlay">
      <div className="reactivate-modal">
        <div className="modal-header">
          <h2>🔄 Reactivate Student</h2>
          <button 
            onClick={onClose} 
            className="close-btn"
            disabled={loading}
          >
            ✕
          </button>
        </div>

        <div className="modal-content">
          <div className="student-info-card">
            <h3>{student.studentName}</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="label">Account Number:</span>
                <span className="value">{student.accountNumber || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="label">School:</span>
                <span className="value">{student.schoolName || 'N/A'}</span>
              </div>
            </div>
          </div>

          {student.deactivationInfo && (
            <div className="deactivation-summary">
              <h4>Previous Deactivation</h4>
              <div className="summary-content">
                <p>
                  <strong>Deactivated On:</strong>{' '}
                  {formatDate(student.deactivationInfo.deactivatedDate)}
                </p>
                <p>
                  <strong>Reason:</strong>{' '}
                  {student.deactivationInfo.reason || 'Not specified'}
                </p>
                {student.deactivationInfo.notes && (
                  <p>
                    <strong>Notes:</strong>{' '}
                    {student.deactivationInfo.notes}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="financial-summary">
            <h4>Current Financial Status</h4>
            <div className="financial-grid">
              <div className="financial-item">
                <span className="financial-label">Total Paid:</span>
                <span className="financial-value paid">
                  {formatCurrency(student.totalPaid)}
                </span>
              </div>
              <div className="financial-item">
                <span className="financial-label">Total Owing:</span>
                <span className="financial-value owing">
                  {formatCurrency(student.totalOwing)}
                </span>
              </div>
            </div>
            {student.totalOwing > 0 && (
              <div className="warning-box">
                <p>⚠️ This student has an outstanding balance. The debt will remain on their account after reactivation.</p>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Reactivation Notes (Optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., Account paid up, Student returning to program..."
              rows="3"
              disabled={loading}
            />
            <small>Add any relevant notes about why the student is being reactivated</small>
          </div>

          <div className="info-box">
            <h4>What happens when you reactivate:</h4>
            <ul>
              <li>✓ Student status changes from Inactive to Active</li>
              <li>✓ Student will appear in "Active Students" filter</li>
              <li>✓ All financial history remains intact</li>
              <li>✓ Outstanding fees (if any) remain collectible</li>
              <li>✓ Deactivation history is preserved for records</li>
            </ul>
          </div>
        </div>

        <div className="modal-actions">
          <button
            onClick={onClose}
            className="btn-cancel"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleReactivate}
            className="btn-reactivate"
            disabled={loading}
          >
            {loading ? 'Reactivating...' : '🔄 Reactivate Student'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ReactivateStudent;